import React, { useState } from 'react';
import { Table, Button, Input, Space, Tag, message, Dropdown, Select, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, MoreOutlined, EditOutlined, EyeOutlined, CopyOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/design-system/Card';
import { templatesApi, DocumentTemplate } from '../services/api';
import dayjs from 'dayjs';

const TYPE_LABELS: Record<string, string> = {
  CONTRACT_SUMMARY: 'Contract Summary',
  BOOKING_REQUEST: 'Booking Request',
  SHIPPING_INSTRUCTION: 'Shipping Instruction',
  COMMERCIAL_INVOICE: 'Commercial Invoice',
  PACKING_LIST: 'Packing List',
  BILL_OF_LADING: 'Bill of Lading',
  BUYER_CONTRACT: 'Buyer Contract',
  SELLER_CONTRACT: 'Seller Contract',
};

const TYPE_COLORS: Record<string, string> = {
  CONTRACT_SUMMARY: 'blue',
  BOOKING_REQUEST: 'cyan',
  SHIPPING_INSTRUCTION: 'purple',
  COMMERCIAL_INVOICE: 'orange',
  PACKING_LIST: 'geekblue',
  BILL_OF_LADING: 'magenta',
  BUYER_CONTRACT: 'green',
  SELLER_CONTRACT: 'gold',
};

const TemplatesList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['templates', searchTerm, typeFilter, statusFilter],
    queryFn: async () => {
      const params: Record<string, unknown> = { limit: 100 };
      if (typeFilter) params.type = typeFilter;
      if (statusFilter !== undefined) params.isActive = statusFilter;
      return templatesApi.list(params);
    },
  });

  const templates = data?.templates || [];

  const filteredTemplates = searchTerm
    ? templates.filter(t =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.description?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : templates;

  const { data: typesData } = useQuery({
    queryKey: ['template-types'],
    queryFn: () => templatesApi.getTypes(),
  });

  const typeOptions = typesData?.types || [];

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return templatesApi.update(id, { isActive });
    },
    onSuccess: () => {
      message.success('Template status updated');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: () => {
      message.error('Failed to update template status');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (template: DocumentTemplate) => {
      return templatesApi.create({
        type: template.type,
        name: `${template.name} (Copy)`,
        description: template.description || undefined,
        content: template.content,
        isDefault: false,
        isActive: false,
      });
    },
    onSuccess: (newTemplate) => {
      message.success('Template duplicated');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      navigate(`/settings/templates/${newTemplate.id}`);
    },
    onError: () => {
      message.error('Failed to duplicate template');
    },
  });

  const handleEdit = (template: DocumentTemplate) => {
    navigate(`/settings/templates/${template.id}`);
  };

  const handlePreview = (template: DocumentTemplate) => {
    navigate(`/settings/templates/${template.id}?preview=true`);
  };

  const handleDuplicate = (template: DocumentTemplate) => {
    duplicateMutation.mutate(template);
  };

  const handleToggleActive = (template: DocumentTemplate) => {
    toggleActiveMutation.mutate({ id: template.id, isActive: !template.isActive });
  };

  const handleCreate = () => {
    navigate('/settings/templates/new');
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 180,
      sorter: (a: DocumentTemplate, b: DocumentTemplate) => a.type.localeCompare(b.type),
      render: (type: string) => (
        <Tag color={TYPE_COLORS[type] || 'default'}>
          {TYPE_LABELS[type] || type}
        </Tag>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: DocumentTemplate, b: DocumentTemplate) => a.name.localeCompare(b.name),
      render: (name: string, record: DocumentTemplate) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (record: DocumentTemplate) => (
        <Space size={4}>
          {record.isActive ? (
            <Tag icon={<CheckCircleOutlined />} color="success">Active</Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="default">Inactive</Tag>
          )}
          {record.isDefault && <Tag color="blue">Default</Tag>}
        </Space>
      ),
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 80,
      render: (version: number) => <code>v{version}</code>,
    },
    {
      title: 'Last Modified',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 140,
      sorter: (a: DocumentTemplate, b: DocumentTemplate) =>
        new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (record: DocumentTemplate) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: () => handleEdit(record),
              },
              {
                key: 'preview',
                icon: <EyeOutlined />,
                label: 'Preview',
                onClick: () => handlePreview(record),
              },
              {
                key: 'duplicate',
                icon: <CopyOutlined />,
                label: 'Duplicate',
                onClick: () => handleDuplicate(record),
              },
              { type: 'divider' },
              {
                key: 'toggle',
                icon: record.isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                label: record.isActive ? 'Deactivate' : 'Activate',
                onClick: () => handleToggleActive(record),
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <h2 className="text-h2" style={{ color: 'var(--color-negative)' }}>Unable to Load Templates</h2>
        <Button type="primary" onClick={() => window.location.reload()} style={{ marginTop: '24px' }}>
          Refresh Page
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 className="text-h1">Document Templates</h1>
          <p className="text-card-body" style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
            Manage templates for contracts, shipping instructions, and other documents
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} size="large">
          New Template
        </Button>
      </div>

      <Card style={{ marginBottom: '24px' }}>
        <Space size="middle" style={{ width: '100%', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search templates..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          <Select
            placeholder="Filter by type"
            value={typeFilter}
            onChange={setTypeFilter}
            allowClear
            style={{ width: 180 }}
            options={typeOptions.map(t => ({ value: t.value, label: t.label }))}
          />
          <Select
            placeholder="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            allowClear
            style={{ width: 120 }}
            options={[
              { value: true, label: 'Active' },
              { value: false, label: 'Inactive' },
            ]}
          />
          <div className="text-card-body" style={{ color: 'var(--text-secondary)' }}>
            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
          </div>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredTemplates}
          loading={isLoading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            pageSizeOptions: [10, 20, 50],
          }}
        />
      </Card>
    </div>
  );
};

export default TemplatesList;
